import { useState, useMemo } from 'react';

import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useParams } from 'react-router-dom';
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, useReactTable } from '@tanstack/react-table';
import { Table_NEW } from '@/components/custom/organisms/Table';

import { useGetProjectsWithoutCrm } from '@/lib/useQuery/project/useProjects';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";

type Props = {
    selectedProjects: { name: string, id: string }[] | [];
    onAddProject?: (project: { name: string, id: string }) => void;
}

const SelectProjectModal: React.FC<Props> = ({
    selectedProjects,
    onAddProject
}) => {
    const { orgId } = useParams();
    const { isLoading: isFetchingProjects, data: projects } = useGetProjectsWithoutCrm({
        organizationId: orgId!,
    });

    const [show, setShow] = useState(false);

    const handleOpen = () => {
        setShow(true);
    }
    const handleClose = () => {
        setShow(false);
    }

    const onSelectProject = (projectId: string) => {
        const project = projects?.find(project => project.id === projectId);
        if(!project || !onAddProject) return;

        onAddProject({
            name: project.name || "",
            id: project.id,
        });

        SuccessPopup("Project has been added to the list.");
    }

    const tableData = useMemo(() => {
        return projects?.filter(project => !selectedProjects.find(selectedProject => selectedProject.id === project.id));
    }, [projects, selectedProjects]);
    const columnHelper = createColumnHelper<{id: string; name: string | null;}>();
    const defaultColumns = [
        columnHelper.display({
            id: 'question',
            header: () => (
                <div className='flex items-center'>
                    <i className={`table-header__text-icon bi bi-file-person-fill me-2 fs-20`}></i>
                    <span className='table-header__text'>Project Name</span>
                </div>
            ),
            cell: (params) => (
                <div className="flex">
                    {params.row.original.name}
                </div>
            )
        }),
        columnHelper.display({
            id: 'actions',
            header: () => (
                <div className='flex items-center'>
                    <i className={`table-header__text-icon bi bi-gear-fill me-2 fs-20`}></i>
                    <span className='table-header__text'>Actions</span>
                </div>
            ),
            cell: (params) => (
                <TooltipProvider>
                    <div className="flex justify-center w-full gap-1" >
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    size='action'
                                    className={`text-nowrap flex text-sm`}
                                    onClick={() => onSelectProject(params.row.original.id)}
                                >
                                    <i className="bi bi-plus-lg"></i>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Select</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                </TooltipProvider>
            ),
            size: 100,
            maxSize: 100,
        }),
    ]

    const table = useReactTable({
        columns: defaultColumns,
        data: tableData ?? [],
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <>
            <Dialog open={show} onOpenChange={handleClose} defaultOpen={false}>
                <DialogContent className="max-w-6xl">
                    <DialogHeader>
                        <DialogTitle>
                            <div className="p-0 m-0 text-start fw-bold">
                                Projects
                            </div>
                        </DialogTitle>
                        <DialogDescription>
                            Select projects to apply CRM configuration.
                        </DialogDescription>
                    </DialogHeader>

                    <ScrollArea className="max-h-[60vh] sm:max-h-[70vh] pe-3">
                        <div className="w-full h-full max-h-80 overflow-scroll-y wissp-scroll-box pe-2 grow">
                            <Table_NEW table={table} />
                        </div>
                    </ScrollArea>

                    <div className="flex justify-end w-full">
                        <div className="w-full mb-1 sm:mb-0 sm:me-2 sm:w-auto">
                            <Button variant="secondary" type="button" onClick={handleClose}>Close</Button>
                        </div>
                    </div>

                </DialogContent>
            </Dialog>

            <Button disabled={isFetchingProjects} onClick={handleOpen}>Connect Existing Project</Button>
        </>
    )
}

export { SelectProjectModal };
